import React, { useMemo, useState } from 'react';
import {
  Box,
  Container,
  Grid,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  RadioGroup,
  FormControlLabel,
  Radio,
  Button,
  Stack,
  Chip,
  IconButton,
  Link as MuiLink,
  Alert,
} from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { styled } from '@mui/material/styles';
import { Delete as DeleteIcon } from '@mui/icons-material';

const KEYWORD_LIMIT = 5;

const FIELD_HINTS = {
  requesterDepartment: 'Tell us which unit or department you represent.',
  requestTitle: 'Provide a descriptive title students will recognize.',
  projectSummary: 'Summarize the problem or opportunity the student team will explore.',
  desiredOutcomes: 'Describe the outcomes you expect the student team to deliver.',
  collaborators: 'List internal or external collaborators (optional).',
  locationDetails: 'Clarify any on-site requirements or constraints.',
  projectRestrictions: 'Outline any prerequisites or boundaries for the work.',
  availableResources: 'List software, datasets, or other resources you can provide.',
  fundingDetails: 'Share compensation details, stipend ranges, or other funding info.',
  specialInstructions: 'Add any extra instructions for submitting proposals.',
};

const StyledTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    backgroundColor: '#ffffff',
    borderRadius: 10,
    transition: 'border-color 0.2s ease',
    '& fieldset': {
      borderColor: 'rgba(80,0,0,0.25)',
    },
    '&:hover fieldset': {
      borderColor: 'rgba(80,0,0,0.6)',
    },
    '&.Mui-focused fieldset': {
      borderColor: '#500000',
      borderWidth: 2,
    },
  },
  '& .MuiInputLabel-root': {
    color: '#500000',
    backgroundColor: '#ffffff',
    padding: '0 8px',
  },
  '& .MuiInputLabel-root.Mui-focused': {
    color: '#500000',
  },
  '& .MuiFormHelperText-root': {
    marginLeft: 0,
  },
  '& .MuiInputBase-input::placeholder': {
    color: 'rgba(80,0,0,0.45)',
    fontStyle: 'italic',
  },
}));

const Section = ({ title, children }) => (
  <Box sx={{ display: 'grid', gap: 2, p: { xs: 2, sm: 3 }, border: '1px solid rgba(80,0,0,0.12)', borderRadius: 2, backgroundColor: '#fff' }}>
    <Typography component="h2" variant="h6" sx={{ fontWeight: 700, color: '#500000', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
      {title}
    </Typography>
    {children}
  </Box>
);

const KeywordEditor = ({
  keywords,
  onAdd,
  onRemove,
  error,
  helperText,
  disabled = false,
}) => {
  const [inputValue, setInputValue] = useState('');

  const remaining = KEYWORD_LIMIT - keywords.length;

  const resolvedHelper = error ? error : helperText || `Add up to ${KEYWORD_LIMIT} keywords (${remaining} remaining).`;

  const tryAddKeyword = () => {
    const trimmed = inputValue.trim();
    if (
      disabled ||
      !trimmed ||
      keywords.includes(trimmed) ||
      keywords.length >= KEYWORD_LIMIT
    ) {
      return;
    }
    onAdd(trimmed);
    setInputValue('');
  };

  return (
    <Box>
      <StyledTextField
        id="projectKeywords"
        name="projectKeywords"
        label="Keywords *"
        value={inputValue}
        onChange={(event) => setInputValue(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === 'Enter') {
            event.preventDefault();
            tryAddKeyword();
          }
        }}
        helperText={resolvedHelper}
        error={Boolean(error)}
        placeholder="Press Enter to add each keyword"
        disabled={disabled}
        fullWidth
      />
      <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: 'wrap', gap: 1 }}>
        {keywords.map((keyword) => (
          <Chip
            key={keyword}
            label={keyword}
            onDelete={
              disabled
                ? undefined
                : () => {
                    onRemove(keyword);
                  }
            }
            sx={{ backgroundColor: '#fbe9e7', color: '#500000', fontWeight: 600 }}
          />
        ))}
      </Stack>
      <Button
        sx={{ mt: 1 }}
        variant="contained"
        color="primary"
        onClick={() => tryAddKeyword()}
        disabled={disabled || !inputValue.trim() || keywords.length >= KEYWORD_LIMIT}
      >
        Add Keyword
      </Button>
    </Box>
  );
};

const DateField = ({ field, label, value, onDateChange, error, helperText }) => (
  <LocalizationProvider dateAdapter={AdapterDateFns}>
    <DatePicker
      label={label}
      closeOnSelect={false}
      value={value ? new Date(`${value}T00:00:00`) : null}
      onChange={(newValue) => onDateChange(field, newValue)}
      slots={{ textField: StyledTextField }}
      slotProps={{
        textField: {
          id: field,
          name: field,
          error: Boolean(error),
          helperText,
          fullWidth: true,
        },
      }}
    />
  </LocalizationProvider>
);

const FilePicker = ({
  label,
  name,
  accept,
  file,
  onChange,
  onRemove,
  inputRef,
  disabled = false,
  helperText,
}) => {
  const hasFile = Boolean(file);
  const resolvedName = typeof file === 'string' ? file.split('/').pop() : file?.name;
  const resolvedLink = typeof file === 'string' ? file : null;

  return (
    <Box>
      <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#500000', mb: 1 }}>
        {label}
      </Typography>
      {helperText && (
        <Typography variant="body2" sx={{ color: 'rgba(80,0,0,0.7)', mb: 1 }}>
          {helperText}
        </Typography>
      )}
      <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap' }}>
        <Button
          component="label"
          variant="contained"
          color="primary"
          sx={{ backgroundColor: '#500000', '&:hover': { backgroundColor: '#3c0000' } }}
        >
          {hasFile ? 'Replace file' : 'Upload file'}
          <input
            id={name}
            name={name}
            hidden
            type="file"
          accept={accept}
          onChange={onChange}
          ref={inputRef}
          disabled={disabled}
        />
      </Button>
      {hasFile && (
        <IconButton
          aria-label={`Remove ${label}`}
          onClick={() => onRemove(name)}
          size="small"
          disabled={disabled}
        >
          <DeleteIcon fontSize="small" />
        </IconButton>
      )}
      </Stack>
      {hasFile && (
        <Typography variant="caption" sx={{ mt: 1, display: 'block' }}>
          {resolvedLink ? (
            <MuiLink href={resolvedLink} target="_blank" rel="noopener noreferrer">
              {resolvedName}
            </MuiLink>
          ) : (
            resolvedName
          )}
        </Typography>
      )}
    </Box>
  );
};

export const NewProjectDialogForm = ({
  asPage = true,
  formData,
  errors,
  characterLimits = {},
  onFieldChange,
  onDateChange,
  onKeywordAdd,
  onKeywordRemove,
  onSave,
  onSubmit,
  onCancelProject,
  onCloseProject,
  onFileChange,
  onFileRemove,
  videoInputRef,
  documentInputRef,
  isPublishDisabled,
  isSaveDisabled,
  isSaving = false,
  isSubmitting = false,
  isCancelling = false,
  isClosing = false,
  isPrefilling = false,
  status,
  feedback,
  onFeedbackClear,
  showValidationErrors = false,
  userInfo,
}) => {
  const [touched, setTouched] = useState({});

  const isBusy = isPrefilling || isSaving || isSubmitting || isCancelling || isClosing;

  const markTouched = (field) => {
    setTouched((prev) => (prev[field] ? prev : { ...prev, [field]: true }));
  };

  const getError = (field) => (touched[field] || showValidationErrors ? errors?.[field] : undefined);

  const getHelperText = (field) => {
    const error = getError(field);
    if (error) return error;

    const base = FIELD_HINTS[field];
    const limit = characterLimits[field];
    if (!base && !limit) return undefined;

    const valueLength = (formData[field] || '').length;
    return limit ? `${base || ''} (${Math.max(limit - valueLength, 0)} characters remaining)` : base;
  };

  const sidebarName = useMemo(() => {
    if (!userInfo) return '';
    return userInfo.DisplayName || `${userInfo.FirstName || ''} ${userInfo.LastName || ''}`.trim();
  }, [userInfo]);

  const sidebarEmail = useMemo(() => {
    if (!userInfo) return '';
    return userInfo.Email || userInfo.email || userInfo.mail || userInfo.preferred_username || '';
  }, [userInfo]);

  const FormContent = (
    <Stack spacing={3}>
      {isPrefilling && !feedback && (
        <Alert severity="info" sx={{ mb: 1 }}>
          Loading project details…
        </Alert>
      )}
      {feedback && (
        <Alert severity={feedback.severity} onClose={onFeedbackClear} sx={{ mb: 1 }}>
          {feedback.message}
        </Alert>
      )}
      {status && (
        <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#500000' }}>
          Current status:{' '}
          <Box component="span" sx={{ fontWeight: 700 }}>
            {status}
          </Box>
        </Typography>
      )}
      <Section title="Employer Information">
        <StyledTextField
          id="requesterName"
          name="requesterName"
          label="Name"
          value={formData.requesterName || sidebarName}
          fullWidth
          disabled
        />
        <FormControl
          fullWidth
          variant="outlined"
          error={Boolean(getError('requesterTitle'))}
        >
          <InputLabel id="requesterTitle-label" htmlFor="requesterTitle">
            Title <span style={{ color: 'red' }}>*</span>
          </InputLabel>
          <Select
            labelId="requesterTitle-label"
            id="requesterTitle"
            name="requesterTitle"
            value={formData.requesterTitle}
            label="Title *"
            onChange={(event) => onFieldChange('requesterTitle', event.target.value)}
            onBlur={() => markTouched('requesterTitle')}
          >
            <MenuItem value="">
              <em>Select title</em>
            </MenuItem>
            <MenuItem value="Mr.">Mr.</MenuItem>
            <MenuItem value="Mrs.">Mrs.</MenuItem>
            <MenuItem value="Ms.">Ms.</MenuItem>
            <MenuItem value="Dr.">Dr.</MenuItem>
            <MenuItem value="Prof.">Prof.</MenuItem>
            <MenuItem value="Mx.">Mx.</MenuItem>
          </Select>
          <FormHelperText>{getError('requesterTitle')}</FormHelperText>
        </FormControl>
        <StyledTextField
          id="requesterDepartment"
          name="requesterDepartment"
          label="Department *"
          value={formData.requesterDepartment}
          onChange={(event) => onFieldChange(event.target.name, event.target.value)}
          onBlur={() => markTouched('requesterDepartment')}
          error={Boolean(getError('requesterDepartment'))}
          helperText={getHelperText('requesterDepartment')}
        />
        <StyledTextField
          id="requesterEmail"
          name="requesterEmail"
          label="Email"
          value={formData.requesterEmail || sidebarEmail}
          fullWidth
          disabled
        />
      </Section>

      <Section title="Project Details">
        <StyledTextField
          id="requestTitle"
          name="requestTitle"
          label="Project Title *"
          value={formData.requestTitle}
          onChange={(event) => onFieldChange(event.target.name, event.target.value)}
          onBlur={() => markTouched('requestTitle')}
          error={Boolean(getError('requestTitle'))}
          helperText={getHelperText('requestTitle')}
          inputProps={{ maxLength: characterLimits.requestTitle }}
        />

        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <DateField
              field="proposalDeadline"
              label="Proposal Deadline *"
              value={formData.proposalDeadline}
              onDateChange={onDateChange}
              error={getError('proposalDeadline')}
              helperText={
                getError('proposalDeadline') ||
                'When are student proposals due? (Must be within 2 months)'
              }
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <DateField
              field="startDate"
              label="Estimated Start Date *"
              value={formData.startDate}
              onDateChange={onDateChange}
              error={getError('startDate')}
              helperText={
                getError('startDate') ||
                'When would you like the project to begin?'
              }
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <DateField
              field="endDate"
              label="Estimated End Date *"
              value={formData.endDate}
              onDateChange={onDateChange}
              error={getError('endDate')}
              helperText={
                getError('endDate') ||
                'When should the project wrap up?'
              }
            />
          </Grid>
        </Grid>

        <KeywordEditor
          keywords={formData.keywords}
          onAdd={(keyword) => {
            markTouched('keywords');
            onKeywordAdd(keyword);
          }}
          onRemove={(keyword) => {
            markTouched('keywords');
            onKeywordRemove(keyword);
          }}
          error={getError('keywords')}
          disabled={isBusy}
        />

        <StyledTextField
          id="projectSummary"
          name="projectSummary"
          label="Project Summary *"
          value={formData.projectSummary}
          onChange={(event) => onFieldChange(event.target.name, event.target.value)}
          onBlur={() => markTouched('projectSummary')}
          error={Boolean(getError('projectSummary'))}
          helperText={getHelperText('projectSummary')}
          inputProps={{ maxLength: characterLimits.projectSummary }}
          multiline
          minRows={4}
        />

        <StyledTextField
          id="desiredOutcomes"
          name="desiredOutcomes"
          label="Desired Outcomes *"
          value={formData.desiredOutcomes}
          onChange={(event) => onFieldChange(event.target.name, event.target.value)}
          onBlur={() => markTouched('desiredOutcomes')}
          error={Boolean(getError('desiredOutcomes'))}
          helperText={getHelperText('desiredOutcomes')}
          inputProps={{ maxLength: characterLimits.desiredOutcomes }}
          multiline
          minRows={3}
        />

        <StyledTextField
          id="collaborators"
          name="collaborators"
          label="Collaborators and Partners"
          value={formData.collaborators}
          onChange={(event) => onFieldChange(event.target.name, event.target.value)}
          onBlur={() => markTouched('collaborators')}
          error={Boolean(getError('collaborators'))}
          helperText={getHelperText('collaborators')}
          inputProps={{ maxLength: characterLimits.collaborators }}
          multiline
          minRows={2}
        />

        <FormControl component="fieldset">
          <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#500000', mb: 1 }}>
            Specific Work Location(s) Required?
          </Typography>
          <RadioGroup
            row
            name="specificLocation"
            value={formData.specificLocation}
            onChange={(event) => onFieldChange('specificLocation', event.target.value)}
          >
            <FormControlLabel value="YES" control={<Radio />} label="Yes" />
            <FormControlLabel value="NO" control={<Radio />} label="No" />
            <FormControlLabel value="MAYBE" control={<Radio />} label="Maybe" />
          </RadioGroup>
        </FormControl>

        {formData.specificLocation === 'YES' && (
          <StyledTextField
            id="locationDetails"
            name="locationDetails"
            label="Location Details"
            value={formData.locationDetails}
            onChange={(event) => onFieldChange(event.target.name, event.target.value)}
            onBlur={() => markTouched('locationDetails')}
            error={Boolean(getError('locationDetails'))}
            helperText={getHelperText('locationDetails')}
            inputProps={{ maxLength: characterLimits.locationDetails }}
            multiline
            minRows={2}
          />
        )}

        <StyledTextField
          id="projectRestrictions"
          name="projectRestrictions"
          label="Project Restrictions"
          value={formData.projectRestrictions}
          onChange={(event) => onFieldChange(event.target.name, event.target.value)}
          onBlur={() => markTouched('projectRestrictions')}
          error={Boolean(getError('projectRestrictions'))}
          helperText={getHelperText('projectRestrictions')}
          inputProps={{ maxLength: characterLimits.projectRestrictions }}
          multiline
          minRows={4}
        />

        <StyledTextField
          id="availableResources"
          name="availableResources"
          label="Available Resources"
          value={formData.availableResources}
          onChange={(event) => onFieldChange(event.target.name, event.target.value)}
          onBlur={() => markTouched('availableResources')}
          error={Boolean(getError('availableResources'))}
          helperText={getHelperText('availableResources')}
          inputProps={{ maxLength: characterLimits.availableResources }}
          multiline
          minRows={3}
        />

        <FormControl component="fieldset">
          <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#500000', mb: 1 }}>
            Student Funding or Salary
          </Typography>
          <RadioGroup
            row
            name="studentFunding"
            value={formData.studentFunding}
            onChange={(event) => onFieldChange('studentFunding', event.target.value)}
          >
            <FormControlLabel value="No Funding Available" control={<Radio />} label="No Funding Available" />
            <FormControlLabel value="Fixed Stipend" control={<Radio />} label="Fixed Stipend" />
            <FormControlLabel value="Hourly Wage" control={<Radio />} label="Hourly Wage" />
            <FormControlLabel value="Other" control={<Radio />} label="Other" />
          </RadioGroup>
        </FormControl>

        {formData.studentFunding !== 'No Funding Available' && (
          <StyledTextField
            id="fundingDetails"
            name="fundingDetails"
            label="Funding Details *"
            value={formData.fundingDetails}
            onChange={(event) => onFieldChange(event.target.name, event.target.value)}
            onBlur={() => markTouched('fundingDetails')}
            error={Boolean(getError('fundingDetails'))}
            helperText={getHelperText('fundingDetails')}
            inputProps={{ maxLength: characterLimits.fundingDetails }}
            multiline
            minRows={2}
          />
        )}

        <StyledTextField
          id="specialInstructions"
          name="specialInstructions"
          label="Special Proposal Submission Instructions"
          value={formData.specialInstructions}
          onChange={(event) => onFieldChange(event.target.name, event.target.value)}
          onBlur={() => markTouched('specialInstructions')}
          error={Boolean(getError('specialInstructions'))}
          helperText={getHelperText('specialInstructions')}
          inputProps={{ maxLength: characterLimits.specialInstructions }}
          multiline
          minRows={3}
        />
      </Section>

      <Section title="Supporting Materials">
        <FilePicker
          label="Supplemental File (PDF)"
          name="supplementalUpload"
          accept=".pdf"
          file={formData.supplementalUpload}
          onChange={onFileChange}
          onRemove={onFileRemove}
          inputRef={documentInputRef}
          disabled={isBusy}
          helperText="Maximum file size: 10 MB"
        />
        <FilePicker
          label="Optional Project Video (MP4)"
          name="proposalVideo"
          accept="video/mp4"
          file={formData.proposalVideo}
          onChange={onFileChange}
          onRemove={onFileRemove}
          inputRef={videoInputRef}
          disabled={isBusy}
          helperText="Maximum file size: 50 MB"
        />
      </Section>

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ justifyContent: 'flex-end' }}>
        <Button
          variant="outlined"
          color="primary"
          onClick={onSave}
          disabled={isSaveDisabled || isBusy}
          sx={{
            borderColor: '#500000',
            color: '#500000',
            fontWeight: 600,
            '&:hover': { borderColor: '#3c0000', backgroundColor: '#fbe9e7' },
            '&.Mui-disabled': { color: '#9e9e9e', borderColor: '#d0d0d0' },
          }}
        >
          {isSaving ? 'Saving…' : 'Save Draft'}
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={onSubmit}
          disabled={isBusy}
          sx={{
            backgroundColor: '#500000',
            fontWeight: 700,
            '&:hover': { backgroundColor: '#3c0000' },
            '&.Mui-disabled': { backgroundColor: '#c8bcbc', color: '#ffffff' },
          }}
        >
          {isSubmitting ? 'Publishing…' : 'Publish Project'}
        </Button>
        {status === 'Saved' && onCancelProject && (
          <Button
            variant="outlined"
            color="secondary"
            onClick={onCancelProject}
            disabled={isBusy}
            sx={{
              borderColor: '#c62828',
              color: '#c62828',
              fontWeight: 600,
              '&:hover': { borderColor: '#8e0000', backgroundColor: '#fdecea' },
              '&.Mui-disabled': { color: '#b0b0b0', borderColor: '#d0d0d0' },
            }}
          >
            {isCancelling ? 'Cancelling…' : 'Cancel Project'}
          </Button>
        )}
        {status === 'Submitted' && onCloseProject && (
          <Button
            variant="contained"
            color="secondary"
            onClick={onCloseProject}
            disabled={isBusy}
            sx={{
              backgroundColor: '#c62828',
              fontWeight: 700,
              '&:hover': { backgroundColor: '#8e0000' },
              '&.Mui-disabled': { backgroundColor: '#d0d0d0', color: '#ffffff' },
            }}
          >
            {isClosing ? 'Closing…' : 'Close Project'}
          </Button>
        )}
      </Stack>
    </Stack>
  );

  if (asPage) {
    return (
      <Container maxWidth="md" sx={{ py: { xs: 2, sm: 4 } }}>
        <Typography variant="h4" sx={{ fontWeight: 700, color: '#500000', mb: 3, textAlign: 'center' }}>
          Submit a Project
        </Typography>
        {FormContent}
      </Container>
    );
  }

  return FormContent;
};
